import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n/config';
import axios from 'axios';
import { Crown, Menu, X, User, LogOut, LayoutDashboard, Shield, Globe, ChevronDown, Coins, MessageCircle, Receipt, Settings, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for navbar transparency
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setCreditBalance(res.data?.balance || 0))
        .catch(() => {});
    }
  }, [user, location.pathname]);

  const navLinks = [
    { href: '/how-it-works', label: t('nav.howItWorks') || 'Aan de Slag' },
    { href: '/credits', label: t('nav.credits') || 'Tokens' },
    { href: '/shop', label: t('nav.shop') },
    { href: '/packages', label: t('nav.packages') },
    { href: '/gallery', label: t('nav.gallery') },
    { href: '/news', label: t('nav.news') },
  ];

  const isActive = (path) => location.pathname === path;
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    // Set RTL for Arabic
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-[#050505]/95 backdrop-blur-xl shadow-lg shadow-black/20 border-b border-white/5' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo with Glamour Styling */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Crown className="w-5 h-5 text-[#D4AF37] group-hover:rotate-12 transition-transform duration-300" />
            </div>
            <span className="text-xl">
              <span className="brand-ella text-white">Ella</span>{' '}
              <span className="brand-gold text-3xl">Gold</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm font-medium tracking-wide transition-all duration-300 ${
                  isActive(link.href)
                    ? 'active text-[#D4AF37]'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden md:flex items-center gap-4">
            {/* Credit Balance (if logged in) */}
            {user && (
              <Link 
                to="/credits"
                className="token-counter hover:scale-105 transition-transform duration-300"
                data-testid="nav-token-balance"
              >
                <Coins className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-semibold">{creditBalance}</span>
              </Link>
            )}

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notifications */}
            {user && <NotificationBell />}

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-gray-400 hover:text-white px-3 py-2 rounded-xl hover:bg-white/5 transition-all duration-300"
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-lg">{currentLang?.flag}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-44 bg-[#0A0A0A]/95 backdrop-blur-xl border-white/10 rounded-xl p-2">
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer rounded-lg hover:bg-white/5 ${i18n.language === lang.code ? 'text-[#D4AF37] bg-[#D4AF37]/10' : ''}`}
                  >
                    <span className="text-lg mr-3">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-3 text-gray-300 hover:text-white px-3 py-2 rounded-xl hover:bg-white/5 transition-all duration-300"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#FCD34D] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                      <User className="w-4 h-4 text-black" />
                    </div>
                    <span className="text-sm font-medium">{user.name}</span>
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-52 bg-[#0A0A0A]/95 backdrop-blur-xl border-white/10 rounded-xl p-2">
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')}
                    className="cursor-pointer rounded-lg hover:bg-white/5 py-3"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-3" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer rounded-lg hover:bg-white/5 py-3"
                    data-testid="menu-profile"
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Mijn Profiel
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/invoices')}
                    className="cursor-pointer rounded-lg hover:bg-white/5 py-3"
                    data-testid="menu-invoices"
                  >
                    <Receipt className="w-4 h-4 mr-3" />
                    Facturen
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/chat')}
                    className="cursor-pointer rounded-lg hover:bg-[#D4AF37]/10 py-3"
                    data-testid="menu-chat"
                  >
                    <MessageCircle className="w-4 h-4 mr-3 text-[#D4AF37]" />
                    <span className="text-[#D4AF37] font-medium">{t('nav.goldChat') || 'Gold Chat'}</span>
                    <Crown className="w-3 h-3 ml-auto text-[#D4AF37]" />
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer rounded-lg hover:bg-white/5 py-3"
                      data-testid="menu-admin"
                    >
                      <Shield className="w-4 h-4 mr-3" />
                      {t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10 my-2" />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-red-400 rounded-lg hover:bg-red-500/10 py-3"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-gray-300 hover:text-white px-5 py-2 rounded-xl hover:bg-white/5 transition-all duration-300"
                  data-testid="nav-login"
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="btn-premium px-6 py-2"
                  data-testid="nav-register"
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            className="md:hidden w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all duration-300"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Full screen overlay with animations */}
      {mobileMenuOpen && (
        <div 
          className="fixed left-0 right-0 z-40 bg-[#050505]/98 backdrop-blur-xl overflow-y-auto block md:hidden animate-fade-in"
          style={{ 
            top: '80px',
            height: 'calc(100vh - 80px)',
            minHeight: 'calc(100vh - 80px)'
          }}
        >
          <div className="px-6 py-8 space-y-6">
            {/* Theme toggle + Credits */}
            <div className="flex items-center justify-between pb-6 border-b border-white/10">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300"
                data-testid="mobile-theme-toggle"
              >
                {isDark ? (
                  <>
                    <Sun className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-sm text-gray-300 font-medium">Licht</span>
                  </>
                ) : (
                  <>
                    <Moon className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-sm text-gray-300 font-medium">Donker</span>
                  </>
                )}
              </button>
              
              {/* Credits (if logged in) */}
              {user && (
                <Link 
                  to="/credits"
                  onClick={() => setMobileMenuOpen(false)}
                  className="token-counter"
                >
                  <Coins className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#D4AF37] font-semibold">{creditBalance}</span>
                </Link>
              )}
            </div>
            
            {/* Language selector */}
            <div className="flex gap-2 pb-6 border-b border-white/10 overflow-x-auto">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    i18n.language === lang.code 
                      ? 'bg-gradient-to-r from-[#D4AF37] to-[#FCD34D] text-black shadow-lg shadow-[#D4AF37]/20' 
                      : 'bg-white/5 text-gray-300 border border-white/10'
                  }`}
                >
                  {lang.flag} {lang.name}
                </button>
              ))}
            </div>
            
            {/* Nav Links */}
            <div className="space-y-2">
              {navLinks.map((link, index) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-4 px-4 rounded-xl text-lg font-medium transition-all duration-300 ${
                    isActive(link.href) 
                      ? 'text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30' 
                      : 'text-gray-300 hover:bg-white/5'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* User Section */}
            <div className="pt-6 border-t border-white/10 space-y-3">
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-4 px-4 rounded-xl text-gray-300 hover:bg-white/5 transition-all duration-300"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    <span className="font-medium">{t('nav.dashboard')}</span>
                  </Link>
                  <Link 
                    to="/profile" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-4 px-4 rounded-xl text-gray-300 hover:bg-white/5 transition-all duration-300"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="font-medium">Mijn Profiel</span>
                  </Link>
                  <Link 
                    to="/invoices" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-4 px-4 rounded-xl text-gray-300 hover:bg-white/5 transition-all duration-300"
                  >
                    <Receipt className="w-5 h-5" />
                    <span className="font-medium">Facturen</span>
                  </Link>
                  <Link 
                    to="/chat" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-4 px-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-semibold">Gold Chat</span>
                    <Crown className="w-4 h-4 ml-auto" />
                  </Link>
                  {user.is_admin && (
                    <Link 
                      to="/admin" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 py-4 px-4 my-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 border border-[#D4AF37]/50 rounded-xl text-[#D4AF37] font-semibold"
                    >
                      <Shield className="w-5 h-5" />
                      Admin Panel
                      <Crown className="w-4 h-4 ml-auto" />
                    </Link>
                  )}
                  <button 
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 py-4 px-4 rounded-xl text-red-400 w-full hover:bg-red-500/10 transition-all duration-300"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  <Button 
                    variant="ghost" 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full justify-center text-gray-300 py-6 text-lg rounded-xl border border-white/10 hover:bg-white/5"
                  >
                    {t('nav.login')}
                  </Button>
                  <Button 
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    className="w-full btn-premium py-6 text-lg"
                  >
                    {t('nav.register')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
